// ---------------------------------------------------------------------------
// YouAreYou (uru) — App Root
//
// Sets up React Router with the full route tree:
//
// Public routes (no auth required):
//   /               → LandingPage (public homepage, redirects to /dashboard if auth'd)
//   /login          → LoginPage (magic link, passkey, SMS OTP)
//   /auth           → Redirects to /login (legacy magic link landing)
//   /register       → RegisterPage
//   /v/:code                    → Public connection link resolution
//   /c/:uuid                    → Public direct link resolution
//   /p/:code                    → Public Verified Human profile page
//   /start-verification/:uuid   → Warm KYC handoff interstitial (SMS invite links)
//
// Public marketing routes (shared MarketingLayout):
//   /how-it-works   → HowItWorksPage
//   /security       → SecurityPage
//   /support        → SupportPage
//   /privacy        → PrivacyPage
//   /terms          → TermsPage
//
// Protected routes (require authentication):
//   /quick-check    → SimpleViewPage (Simple Mode — share verification link)
//   /dashboard      → DashboardPage (Create Trust Link)
//   /connections    → ConnectionsPage (Contacts Directory)
//   /connections/:id → ConnectionsPage (opens Contact Detail Modal)
//   /requests       → RequestsPage
//   /settings       → SettingsPage
//   /activity       → ActivityPage (full connection history)
//
// Catch-all:
//   *              → NotFoundPage (branded 404)
// ---------------------------------------------------------------------------

import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ModeProvider } from "@/context/ModeContext";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import EnvBanner from "@/components/ui/EnvBanner";
import LoginPage from "@/pages/auth/LoginPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import DashboardTestPage from "@/pages/dashboard/DashboardTestPage";
import ConnectionsPage from "@/pages/connections/ConnectionsPage";
import ActivityPage from "@/pages/activity/ActivityPage";
import NotFoundPage from "@/pages/public/NotFoundPage";
import {
  MarketingLayout,
  HowItWorksPage,
  SecurityPage,
  SupportPage,
  PrivacyPage,
  TermsPage,
} from "@/pages/public/MarketingPages";
import ConnectionRequestPage from "@/pages/public/ConnectionRequestPage";
// Code-split: the recipient signing page pulls in pdf.js (~150KB gzip), which
// only signers need — keep it out of the main bundle.
const SignDocumentPage = lazy(() => import("@/pages/public/SignDocumentPage"));
import VerifiedHumanPage from "@/pages/public/VerifiedHumanPage";
import LandingPage from "@/pages/public/LandingPage";
import RealityCheckPage from "@/pages/public/RealityCheckPage";
import StartVerificationPage from "@/pages/public/StartVerificationPage";
import EngagedViewPage from "@/pages/public/EngagedViewPage";
import VerifiedHumanDemoPage from "@/pages/public/VerifiedHumanDemoPage";
import SimpleViewPage from "@/pages/dashboard/SimpleViewPage";
import SettingsPage from "@/pages/settings/SettingsPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import RequestsPage from "@/pages/requests/RequestsPage";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import VerifyEmbedPage from "@/pages/public/VerifyEmbedPage";
import PasskeyRegistrationPrompt from "@/components/ui/PasskeyRegistrationPrompt";


// ---------------------------------------------------------------------------
// Root route: always shows the landing page regardless of auth state.
// ---------------------------------------------------------------------------

function RootRoute() {
  return <LandingPage />;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ModeProvider>
        <EnvBanner />
        <PasskeyRegistrationPrompt />
        <Routes>
          {/* ---- Public Routes ---- */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/verify" element={<RegisterPage />} />
          <Route path="/v/:code" element={<ConnectionRequestPage />} />
          <Route path="/c/:uuid" element={<ConnectionRequestPage />} />
          <Route
            path="/sign/:uuid"
            element={
              <Suspense fallback={<div className="min-h-dvh bg-background-light" />}>
                <SignDocumentPage />
              </Suspense>
            }
          />
          {/* Branded recipient shell embedding the real verify form.
              NB: path must NOT start with /verify — nginx proxies /verify/* to the
              MojoVerify backend, so a /verify/:uuid SPA route 404s on direct load. */}
          <Route path="/id/:uuid" element={<VerifyEmbedPage />} />
          <Route path="/p/:code" element={<VerifiedHumanPage />} />
          <Route path="/connect/verify" element={<ConnectionRequestPage />} />
          <Route path="/start-verification/:uuid" element={<StartVerificationPage />} />
          <Route path="/engage/:uuid" element={<EngagedViewPage />} />
          <Route path="/quick-check/demo" element={<VerifiedHumanDemoPage />} />
          <Route path="/reality-check" element={<RealityCheckPage />} />

          {/* ---- Public Marketing Routes (shared layout) ---- */}
          <Route element={<MarketingLayout />}>
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Route>

          {/* ---- Protected Routes (require authentication) ---- */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/quick-check" element={<SimpleViewPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard-test" element={<DashboardTestPage />} />
              <Route path="/connections" element={<ConnectionsPage />} />
              <Route path="/connections/:id" element={<ConnectionsPage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/activity" element={<ActivityPage />} />
            </Route>
          </Route>

          {/* ---- Root — Landing page (public) or redirect (authenticated) ---- */}
          <Route path="/" element={<RootRoute />} />

          {/* ---- 404 catch-all ---- */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
